import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import Input from './Input';

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const NamesRow = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md};
  border-bottom: 1px solid ${theme.colors.border};
  flex-shrink: 0;
`;

const NameField = styled.label`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSizes.xs};
  color: ${theme.colors.textMuted};
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const RulesRow = styled.div`
  display: grid;
  grid-template-columns: 5.5rem 1fr;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-bottom: 1px solid ${theme.colors.border};
  flex-shrink: 0;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSizes.xs};
  color: ${theme.colors.textMuted};
`;

const RulesLabel = styled.span`
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const ServerToggle = styled.div`
  display: flex;
  min-width: 0;
  border: 1px solid ${(p) => (p.$unset ? theme.colors.warning : theme.colors.border)};
  border-radius: 3px;
  overflow: hidden;
`;

const ServerOption = styled.button`
  flex: 1;
  min-width: 0;
  border: none;
  padding: 2px 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  font-family: ${theme.typography.fontFamily};
  font-size: 10px;
  font-weight: ${theme.typography.fontWeights.semibold};
  background: ${(p) => (p.$active ? p.$color : 'transparent')};
  color: ${(p) => (p.$active ? theme.colors.background : theme.colors.textMuted)};
  transition: ${theme.transitions.fast};

  &:hover { color: ${(p) => (p.$active ? theme.colors.background : theme.colors.text)}; }
`;

const MarkSection = styled.div`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-bottom: 1px solid ${theme.colors.border};
  flex-shrink: 0;
`;

const MarkButton = styled.button`
  width: 100%;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${(p) => (p.$pending ? theme.colors.warning : theme.colors.primary)};
  background: ${(p) => (p.$pending ? `${theme.colors.warning}22` : `${theme.colors.primary}22`)};
  color: ${(p) => (p.$pending ? theme.colors.warning : theme.colors.primary)};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSizes.base};
  font-weight: ${theme.typography.fontWeights.semibold};
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.disabled ? 0.45 : 1)};
  transition: ${theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${(p) => (p.$pending ? `${theme.colors.warning}33` : `${theme.colors.primary}33`)};
  }
`;

const MarkHint = styled.div`
  margin-top: ${theme.spacing.xs};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.sm};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSizes.xs};
  color: ${(p) => (p.$error ? theme.colors.error : theme.colors.textMuted)};
  min-height: 14px;
`;

const CancelLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSizes.xs};
  text-decoration: underline;
  flex-shrink: 0;

  &:hover { color: ${theme.colors.text}; }
`;

const ListScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${theme.spacing.xs} 0;
  scroll-padding: ${theme.spacing.xs} 0;
  scroll-snap-type: y mandatory;
`;

const RallyItem = styled.div`
  position: relative;
  scroll-snap-align: start;
`;

const EmptyMsg = styled.div`
  padding: ${theme.spacing.md};
  color: ${theme.colors.textMuted};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSizes.sm};
  text-align: center;
  line-height: 1.5;
`;

const RallyRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  width: 100%;
  box-sizing: border-box;
  padding: 3px ${theme.spacing.md};
  cursor: pointer;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSizes.sm};
  color: ${theme.colors.textSecondary};
  background: ${(p) => (p.$awaiting ? 'rgba(255, 208, 64, 0.1)' : 'transparent')};
  transition: background ${theme.transitions.fast};

  &:hover { background: rgba(114, 210, 255, 0.07); }
`;

const RallyIndex = styled.span`
  width: 22px;
  flex-shrink: 0;
  color: ${theme.colors.textMuted};
`;

const RallyTimes = styled.span`
  font-variant-numeric: tabular-nums;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UnsetMark = styled.span`
  flex-shrink: 0;
  color: ${theme.colors.warning};
  font-weight: ${theme.typography.fontWeights.bold};
`;

const ScoreBadge = styled.span`
  flex-shrink: 0;
  min-width: 38px;
  text-align: center;
  padding: 1px 4px;
  border-radius: 3px;
  background: ${(p) => (p.$color ? `${p.$color}26` : 'rgba(255, 255, 255, 0.06)')};
  color: ${(p) => p.$color || theme.colors.textMuted};
  font-size: 10px;
  font-variant-numeric: tabular-nums;
`;

const GameBadge = styled(ScoreBadge)`
  min-width: 26px;
`;

const WinnerToggle = styled.div`
  display: flex;
  flex-shrink: 0;
  border: 1px solid ${(p) => (p.$unset ? theme.colors.warning : theme.colors.border)};
  border-radius: 3px;
  overflow: hidden;
`;

const WinnerOption = styled.button`
  border: none;
  padding: 1px 6px;
  max-width: 62px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  font-family: ${theme.typography.fontFamily};
  font-size: 10px;
  font-weight: ${theme.typography.fontWeights.semibold};
  background: ${(p) => (p.$active ? p.$color : 'transparent')};
  color: ${(p) => (p.$active ? theme.colors.background : theme.colors.textMuted)};
  transition: ${theme.transitions.fast};

  &:hover { color: ${(p) => (p.$active ? theme.colors.background : theme.colors.text)}; }
`;

const EllipsisBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  padding: 0 4px;
  font-size: 13px;
  line-height: 1;
  border-radius: 3px;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: ${theme.colors.text};
  }
`;

const RowMenu = styled.div`
  position: absolute;
  right: ${theme.spacing.md};
  top: 100%;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: 4px;
  box-shadow: ${theme.shadows.md};
  z-index: 100;
  min-width: 170px;
  overflow: hidden;
`;

const RowMenuItem = styled.button`
  display: block;
  width: 100%;
  padding: 7px 12px;
  background: none;
  border: none;
  text-align: left;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSizes.xs};
  color: ${(p) => (p.$danger ? theme.colors.error : theme.colors.text)};
  cursor: pointer;

  &:hover { background: rgba(114, 210, 255, 0.1); }
`;

const Footer = styled.div`
  flex-shrink: 0;
  border-top: 1px solid ${theme.colors.border};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const FooterStatus = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSizes.xs};
  color: ${(p) => (p.$warn ? theme.colors.warning : theme.colors.textMuted)};
  min-height: 14px;
`;

const FooterButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const FooterButton = styled.button`
  flex: 1;
  padding: 5px 0;
  border-radius: ${theme.borderRadius.sm};
  border: 1px solid ${(p) => (p.$primary ? theme.colors.primary : theme.colors.border)};
  background: ${(p) => (p.$primary ? `${theme.colors.primary}22` : 'transparent')};
  color: ${(p) => (p.$primary ? theme.colors.primary : theme.colors.textSecondary)};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSizes.sm};
  cursor: pointer;
  transition: ${theme.transitions.fast};

  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
`;

// ---------------------------------------------------------------------------

const NEAR_COLOR = theme.colors.playerGreen;
const FAR_COLOR = theme.colors.playerRed;

const GAME_COLORS = ['#72d2ff', '#ffd040', '#bd93f9', '#50fa7b', '#ff79c6'];

const gameColor = (score) => {
  if (!score || score.gameNumber === null) return null;
  return GAME_COLORS[score.gameNumber % GAME_COLORS.length];
};

const WHEEL_STEP_THRESHOLD = 25;

const formatTime = (t) => {
  const mins = Math.floor(t / 60);
  const secs = (t % 60).toFixed(2).padStart(5, '0');
  return `${String(mins).padStart(2, '0')}:${secs}`;
};

/** Short badge text: the point score entering the rally, TB-prefixed in a tiebreak. */
const badgeText = (score) => {
  if (!score || score.pointScore === null) return '—';
  return score.isTiebreak ? `TB ${score.pointScore}` : score.pointScore;
};

const gameBadgeText = (score) => {
  if (!score || score.gameScore === null) return '—';
  return score.gameScore;
};

/**
 * The rally list is split out and memo'd so that the pending-rally readout,
 * which ticks with the video, cannot re-render every row on every frame.
 */
const RallyList = React.memo(({
  rallies,
  scoreboard,
  awaitingWinnerId,
  nearLabel,
  farLabel,
  onSeek,
  onSetWinner,
  onSetTime,
  onDelete,
}) => {
  const [openMenuId, setOpenMenuId] = useState(null);
  const listRef = useRef(null);
  const hasRallies = rallies.length > 0;

  useEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    let accumulatedDelta = 0;

    const handleWheel = (e) => {
      const firstItem = list.firstElementChild;
      if (!firstItem) return;
      e.preventDefault();

      accumulatedDelta += e.deltaMode === 0 ? e.deltaY : Math.sign(e.deltaY) * WHEEL_STEP_THRESHOLD;
      if (Math.abs(accumulatedDelta) < WHEEL_STEP_THRESHOLD) return;

      const direction = Math.sign(accumulatedDelta);
      accumulatedDelta = 0;
      const rowHeight = firstItem.offsetHeight;
      const currentRow = Math.round(list.scrollTop / rowHeight);
      list.scrollTo({ top: (currentRow + direction) * rowHeight });
    };

    list.addEventListener('wheel', handleWheel, { passive: false });
    return () => list.removeEventListener('wheel', handleWheel);
  }, [hasRallies]);

  // Keyed by rally rather than by position so a row can never show a
  // neighbour's score if the two lists ever fall out of step.
  const scoreById = useMemo(
    () => new Map(scoreboard.map((entry) => [entry.id, entry])),
    [scoreboard]
  );

  if (!hasRallies) {
    return (
      <EmptyMsg>
        No rallies yet.
        <br />
        Press Start rally at the first serve.
      </EmptyMsg>
    );
  }

  return (
    <ListScroll ref={listRef}>
      {rallies.map((rally, index) => {
        const menuOpen = openMenuId === rally.id;
        const score = scoreById.get(rally.id);
        const color = gameColor(score);
        return (
          <RallyItem key={rally.id}>
            <RallyRow
              $awaiting={rally.id === awaitingWinnerId}
              onClick={() => { setOpenMenuId(null); onSeek(rally.startTime); }}
              title={`Jump to ${formatTime(rally.startTime)}`}
            >
              <RallyIndex>{index + 1}</RallyIndex>
              <RallyTimes>
                {formatTime(rally.startTime)} → {formatTime(rally.endTime)}
              </RallyTimes>
              {rally.winner === null && <UnsetMark title="No winner picked yet">?</UnsetMark>}
              <GameBadge
                $color={color}
                onClick={(e) => e.stopPropagation()}
              >
                {gameBadgeText(score)}
              </GameBadge>
              <ScoreBadge
                $color={color}
                onClick={(e) => e.stopPropagation()}
              >
                {badgeText(score)}
              </ScoreBadge>
              <WinnerToggle $unset={rally.winner === null}>
                <WinnerOption
                  $active={rally.winner === 'near'}
                  $color={NEAR_COLOR}
                  title={`${nearLabel} won this rally`}
                  onClick={(e) => { e.stopPropagation(); onSetWinner(rally.id, 'near'); }}
                >
                  {nearLabel}
                </WinnerOption>
                <WinnerOption
                  $active={rally.winner === 'far'}
                  $color={FAR_COLOR}
                  title={`${farLabel} won this rally`}
                  onClick={(e) => { e.stopPropagation(); onSetWinner(rally.id, 'far'); }}
                >
                  {farLabel}
                </WinnerOption>
              </WinnerToggle>
              <EllipsisBtn
                title="Rally options"
                onClick={(e) => { e.stopPropagation(); setOpenMenuId(menuOpen ? null : rally.id); }}
              >
                ⋯
              </EllipsisBtn>
            </RallyRow>
            {menuOpen && (
              <RowMenu>
                <RowMenuItem
                  onClick={() => { onSeek(rally.endTime); setOpenMenuId(null); }}
                >
                  Jump to rally end
                </RowMenuItem>
                <RowMenuItem
                  onClick={() => { onSetTime(rally.id, 'start'); setOpenMenuId(null); }}
                >
                  Set start to playhead
                </RowMenuItem>
                <RowMenuItem
                  onClick={() => { onSetTime(rally.id, 'end'); setOpenMenuId(null); }}
                >
                  Set end to playhead
                </RowMenuItem>
                <RowMenuItem
                  $danger
                  onClick={() => { onDelete(rally.id); setOpenMenuId(null); }}
                >
                  Delete rally
                </RowMenuItem>
              </RowMenu>
            )}
          </RallyItem>
        );
      })}
    </ListScroll>
  );
});

/**
 * Rally scoring panel — shown in the sidebar while the app is in rally mode.
 *
 * `currentTime` is only read for the pending-rally readout; the rally list is
 * memo'd separately so it does not re-render on every video frame.
 */
const RallyPanel = ({
  nearPlayer,
  farPlayer,
  onNearPlayerChange,
  onFarPlayerChange,
  initialServer,
  onInitialServerChange,
  adScoring,
  onAdScoringChange,
  rallies,
  scoreboard,
  pendingStart,
  awaitingWinnerId,
  incompleteRallies,
  currentTime,
  markError,
  onStartRally,
  onEndRally,
  onCancelPendingRally,
  onSetWinner,
  onSetRallyTime,
  onDeleteRally,
  onSeek,
  onSave,
  onLoad,
  loadedFileName,
}) => {
  const fileInputRef = useRef(null);
  const pending = pendingStart !== null;
  const namesReady = nearPlayer.trim().length > 0 && farPlayer.trim().length > 0;
  // Without the opening server the score cannot be derived, so a rally started
  // now would be labelled with a blank badge.
  const setupReady = namesReady && initialServer !== null;

  const nearLabel = nearPlayer.trim() || 'Near';
  const farLabel = farPlayer.trim() || 'Far';

  const status = useMemo(() => {
    if (rallies.length === 0) return { text: loadedFileName ? `Loaded: ${loadedFileName}` : '', warn: false };
    if (incompleteRallies.length > 0) {
      const list = incompleteRallies.join(', ');
      return { text: `${rallies.length} rallies — no winner on ${list}`, warn: true };
    }
    return { text: `${rallies.length} rallies, all scored`, warn: false };
  }, [rallies.length, incompleteRallies, loadedFileName]);

  return (
    <>
      <NamesRow>
        <NameField>
          Near (bottom)
          <Input
            value={nearPlayer}
            onChange={(e) => onNearPlayerChange(e.target.value)}
            placeholder="Player name"
            padding="5px 8px"
            fontSize={theme.typography.fontSizes.sm}
          />
        </NameField>
        <NameField>
          Far (top)
          <Input
            value={farPlayer}
            onChange={(e) => onFarPlayerChange(e.target.value)}
            placeholder="Player name"
            padding="5px 8px"
            fontSize={theme.typography.fontSizes.sm}
          />
        </NameField>
      </NamesRow>

      <RulesRow>
        <RulesLabel>Starting server</RulesLabel>
        <ServerToggle $unset={initialServer === null}>
          <ServerOption
            $active={initialServer === 'near'}
            $color={NEAR_COLOR}
            title={`${nearLabel} was the starting server`}
            onClick={() => onInitialServerChange('near')}
          >
            {nearLabel}
          </ServerOption>
          <ServerOption
            $active={initialServer === 'far'}
            $color={FAR_COLOR}
            title={`${farLabel} was the starting server`}
            onClick={() => onInitialServerChange('far')}
          >
            {farLabel}
          </ServerOption>
        </ServerToggle>
      </RulesRow>

      <RulesRow>
        <RulesLabel>Scoring</RulesLabel>
        <ServerToggle>
          <ServerOption
            $active={!adScoring}
            $color={theme.colors.primary}
            title="40-40 is a single deciding point"
            onClick={() => onAdScoringChange(false)}
          >
            No-ad
          </ServerOption>
          <ServerOption
            $active={adScoring}
            $color={theme.colors.primary}
            title="40-40 goes to advantage"
            onClick={() => onAdScoringChange(true)}
          >
            Ad
          </ServerOption>
        </ServerToggle>
      </RulesRow>

      <MarkSection>
        <MarkButton
          $pending={pending}
          disabled={!pending && !setupReady}
          onClick={pending ? onEndRally : onStartRally}
          title={
            pending
              ? 'End the rally at the playhead'
              : setupReady
                ? 'Start a rally at the playhead'
                : namesReady
                  ? 'Pick the starting server'
                  : 'Enter both player names first'
          }
        >
          {pending ? `End rally @ ${formatTime(currentTime)}` : 'Start rally'}
        </MarkButton>
        <MarkHint $error={Boolean(markError)}>
          <span>
            {markError
              ? markError
              : pending
                ? `Started at ${formatTime(pendingStart)} — play to the last shot, then end.`
                : !namesReady
                  ? 'Enter both player names before starting.'
                  : initialServer === null
                    ? 'Pick the starting server before starting.'
                    : ''}
          </span>
          {pending && <CancelLink onClick={onCancelPendingRally}>Cancel</CancelLink>}
        </MarkHint>
      </MarkSection>

      <RallyList
        rallies={rallies}
        scoreboard={scoreboard}
        awaitingWinnerId={awaitingWinnerId}
        nearLabel={nearLabel}
        farLabel={farLabel}
        onSeek={onSeek}
        onSetWinner={onSetWinner}
        onSetTime={onSetRallyTime}
        onDelete={onDeleteRally}
      />

      <Footer>
        <FooterStatus $warn={status.warn}>{status.text}</FooterStatus>
        <FooterButtons>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onLoad(file);
              e.target.value = '';
            }}
          />
          <FooterButton onClick={() => fileInputRef.current?.click()}>
            Load rallies
          </FooterButton>
          <FooterButton $primary onClick={onSave} title="Save rallies JSON (S)">
            Save rallies
          </FooterButton>
        </FooterButtons>
      </Footer>
    </>
  );
};

export default RallyPanel;
